/* eslint-disable no-case-declarations */
/* eslint-disable no-param-reassign */
/* eslint-disable no-return-assign */
import {
  PLAN_CATEGORIES,
  getOfferName,
  getLeadGenerator,
  getOrderType,
  IS_GROUP_MEMBER_VALUES,
  returnV2CalculatedTax,
  getDiscountValue,
  SIM_JOURNEY_TYPES,
} from '@smarty-nx/code-utils'
import omit from 'lodash/omit'

import { DATA_LAYER_EVENTS, DATA_LAYER_SCREEN_TYPES } from '../constants'
import {
  findPageAttrs,
  removeTrailingSlash,
  convertDataAttributeToObjects,
  triggerEvent,
} from '../utils'
import {
  AddonProductObject,
  DataLayerEvent,
  DiscountData,
  PaymentData,
  ProductObject,
  Smdl,
  SmdlModule,
  SmdlScreen,
  SmdlUser,
  ModuleItem,
  ValueObject,
  DataLayerObject,
  SmdlPlatform,
  AssetItem,
  SmdlGroup,
  SmdlGroupInfo,
  SmdlAssets,
} from '../types'
import { PathMapEntry } from '../utils'
import { Plan } from '@smarty-nx/data'

const { CHECKOUT, HOMEPAGE, PRODUCT_LIST, PRODUCT_DETAILS } =
  DATA_LAYER_SCREEN_TYPES

export const getSimType = () => {
  const state = JSON.parse(localStorage.getItem('state') || '{}')
  const sessionState = JSON.parse(sessionStorage.getItem('state') || '{}')

  if (state?.auth?.jwt) {
    return state?.simType
  }

  if (sessionState?.auth?.jwt) {
    return sessionState?.simType
  }
}

export const buildUser = (): SmdlUser => {
  const state = JSON.parse(localStorage.getItem('state') || '{}')
  const sessionState = JSON.parse(sessionStorage.getItem('state') || '{}')
  const offer = getOfferName(state?.currentPlan)

  if (state && state.auth && state.auth.jwt) {
    const user = {
      status: 'logged-in',
      customer: {
        id: state.customerId,
        plan: state.customerPlan,
        plan_status: state.planStatus,
        ...(state.currentPlan && {
          offer_name: offer,
        }),
        order_created_at: state.orderCreatedAt,
      },
    }

    if (state.actingAs !== null) {
      user.customer.actingAs = state.actingAs
      if (state.currentPlan) {
        user.customer.member_offer_name = offer
      }
    }

    return user as SmdlUser
  }

  if (sessionState && sessionState.auth && sessionState.auth.jwt) {
    return {
      status: 'logged-in',
      customer: {
        id: sessionState.customerId,
        plan: sessionState.customerPlan,
        plan_status: sessionState.planStatus,
        ...(sessionState.currentPlan && {
          offer_name: getOfferName(state.currentPlan),
        }),
        order_created_at: sessionState.orderCreatedAt,
      },
    }
  } else if (state && state.journey && state.journey.isPaypointJourney) {
    return {
      status: 'logged-out',
      customer: {
        paypoint: 'true',
      },
    } as SmdlUser
  }
  return {
    status: 'logged-out',
  }
}

export const buildUserWithOrderType = (orderType?: string): SmdlUser => {
  const user = buildUser()
  if (user.status !== 'logged-out' && orderType) {
    user.customer.order_type = orderType
  }

  return user
}

export const getIsGroupMemberValue = (
  isOwner: boolean | null,
  isMember: boolean | null
): IS_GROUP_MEMBER_VALUES => {
  switch (true) {
    case isOwner:
      return IS_GROUP_MEMBER_VALUES.YES_OWNER
    case isMember:
      return IS_GROUP_MEMBER_VALUES.YES
    default:
      return IS_GROUP_MEMBER_VALUES.NO
  }
}

export const getGroupAccountType = (
  isMemberUnder16: boolean,
  memberNickname: string,
  authenticated: boolean
) =>
  !isMemberUnder16 && memberNickname
    ? 'device'
    : authenticated
    ? 'existing-smarty-client'
    : 'new-person'

export const buildGroupInfo = (
  inviteType: string | null = null
): SmdlGroupInfo => {
  const state = JSON.parse(localStorage.getItem('state') || '{}')
  const sessionState = JSON.parse(sessionStorage.getItem('state') || '{}')
  const authenticated =
    (state?.auth && state.auth.jwt) ||
    (sessionState?.auth && sessionState.auth.jwt)

  const group: SmdlGroup = {
    id: null,
    invitations: 0,
    groupSize: 0,
    isGroupMember: getIsGroupMemberValue(
      state.isGroupOwner,
      state.isGroupMember
    ),
  }

  if (state.groupId) {
    group.id = state.groupId
    group.invitations = state.groupInvitations || 0
    group.groupSize = state.groupSize || 0
    group.groupAccountType = getGroupAccountType(
      state.isMemberUnder16,
      state.memberNickname,
      authenticated
    )
    group.isOverSixteen = state.isMemberUnder16 ? 'no' : 'yes'
  }

  const groupInfo: SmdlGroupInfo = {
    group,
    inviteType,
  }

  return groupInfo
}

let pathMap: Record<string, PathMapEntry>,
  regexPathMap: Record<string, PathMapEntry>
export const setDmpgObject = ({
  pathMap: appPathMap,
  regexPathMap: appRegexPathMap,
  platform,
}: {
  pathMap: Record<string, PathMapEntry>
  regexPathMap: Record<string, PathMapEntry>
  platform: SmdlPlatform
}) => {
  pathMap = appPathMap
  regexPathMap = appRegexPathMap
  if (!window.smDl) {
    window.smDl = {
      events: [],
      platform,
    }
  }
  window.smDl.user = buildUser()
  window.smDl.screen = getScreenFromLocation()
  window.smDl.groupInfo = buildGroupInfo()
  buildEventsBasedOnScreenType(window.smDl.screen, document, false)
  attachAllClickEvents(document, window.smDl)
}

const elements = (
  parent: Document | HTMLElement,
  selector: string
): HTMLElement[] => Array.from(parent.querySelectorAll(selector))

const element = (
  parent: Document | HTMLElement,
  selector: string
): HTMLElement | null => parent.querySelector(selector)

export const findProducts = (
  parentEl: Document | HTMLElement = document
): (ProductObject | AddonProductObject | undefined)[] =>
  elements(parentEl, '[data-product_id]')
    .map(convertDataAttributeToObjects)
    .map(({ product }: DataLayerObject) => product)

const buildDiscount = (discount?: DiscountData) => {
  if (discount) {
    const { displayGross, displayTax } = discount.value
    return {
      discount: {
        lines: [
          {
            ...discount,
            value: {
              displayGross: Number(displayGross),
              displayTax: Number(displayTax),
            },
          },
        ],
      },
    }
  }
  return undefined
}

/**
 * OLD function which uses data attributes in the checkout pages
 * to build a cart object with product data.
 * Replaced by buildCartData in buildEventData.ts
 * */
export const buildCart = (parentEl: Document | HTMLElement) => {
  const cartEl = element(parentEl, '[data-cart_checkout-step]')
  if (cartEl) {
    const { cart, leadGenerator, orderType, selectedPortInOption } =
      convertDataAttributeToObjects(cartEl)

    const productLines = elements(parentEl, '[data-lines_id]')
      .map(convertDataAttributeToObjects)
      .map(({ lines, discount }) => {
        const {
          displayGross,
          displayTax,
          discount: lineDiscount,
        } = lines?.value || {}
        return {
          product: {
            ...lines,
            quantity: Number(lines?.quantity),
            value: {
              displayGross: Number(displayGross),
              displayTax: Number(displayTax),
            },
            ...(lineDiscount && {
              ...lineDiscount,
              value: {
                displayGross: Number(lineDiscount.value.displayGross),
                ...(lineDiscount.value.displayTax && {
                  displayTax: Number(lineDiscount.value.displayTax),
                }),
              },
            }),
          },
          ...buildDiscount(discount),
        }
      })

    return {
      ...cart,
      lines: productLines,
      leadGenerator,
      orderType,
      selectedPortInOption,
      ...(cart?.value
        ? {
            value: {
              displayGross: Number((cart.value as ValueObject).displayGross),
              displayTax: Number((cart.value as ValueObject).displayTax),
            },
          }
        : {}),
    }
  }

  return undefined
}

const buildPaymentArray = (
  parentEl: Document | HTMLElement,
  categoryType?: string
): PaymentData[] => {
  const paymentEl = element(parentEl, '[data-payment-credit]')
  const paymentElPreloaded = element(
    parentEl,
    '[data-payment-credit-preloaded]'
  )
  let payments: PaymentData[] = []
  if (paymentEl) {
    payments = [
      {
        type: 'credit',
        value: {
          displayGross: Number(paymentEl.getAttribute('data-payment-credit')),
        },
      },
      {
        type: paymentEl.getAttribute('data-payment-method'),
        value: {
          displayGross:
            categoryType === PLAN_CATEGORIES.PRELOADED_SIM
              ? 0
              : Number(paymentEl.getAttribute('data-payment-paid')),
        },
      },
    ]
  }
  if (paymentElPreloaded) {
    const item = {
      type: 'credit_preloaded',
      value: {
        displayGross: Number(
          paymentElPreloaded.getAttribute('data-payment-credit-preloaded')
        ),
      },
    }
    payments = Array.isArray(payments) ? payments.concat(item) : [item]
  }
  return payments
}

const buildTransaction = (parentEl: Document | HTMLElement) => {
  const transactionEl = element(parentEl, '[data-transaction_id]')
  if (transactionEl) {
    const { transaction } = convertDataAttributeToObjects(transactionEl)
    return transaction
  }

  return undefined
}

const buildItem = (el: HTMLElement): ModuleItem => ({
  id: el.dataset.itemId,
  type: el.getAttribute('data-type'),
  position: el.getAttribute('data-position'),
})

const findItem = (
  module: Document | HTMLElement,
  slug?: string
): ModuleItem[] =>
  slug
    ? elements(module, `[data-item-id="${slug}"]`).map(buildItem)
    : elements(module, '[data-item-id]').map(buildItem)

const findModules = (
  parentEl: Document | HTMLElement = document,
  slug?: string
): SmdlModule[] =>
  elements(parentEl, '[data-module-id]').map((el: HTMLElement) => ({
    ...convertDataAttributeToObjects(el).module,
    id: el.getAttribute('data-module-id'),
    items: findItem(el, slug),
  }))

const buildItemWithAssetData = (el: HTMLElement) => {
  const { asset, type, discount } = convertDataAttributeToObjects(el)
  return {
    ...asset,
    ...buildDiscount(discount),
    type,
    id: el.getAttribute('data-item-id'),
  }
}

const findAssets = (
  parentEl: Document | HTMLElement = document,
  slug?: string
) => {
  const itemIdQuery = slug ? `[data-item-id="${slug}"]` : '[data-item-id]'
  return elements(parentEl, itemIdQuery)
    .map(buildItemWithAssetData)
    .reduce((acc, item) => {
      if (item.type) {
        if (!acc[item.type]) {
          acc[item.type] = []
        }
        acc[item.type].push(omit(item, ['type']))
      }
      return acc
    }, {} as AssetItem)
}

export const moduleEvent = (
  container: Document | HTMLElement,
  slug?: string
): DataLayerEvent => ({
  event: DATA_LAYER_EVENTS.MODULE_LOAD,
  modules: findModules(container, slug),
  assets: findAssets(container, slug),
})

const productEvent = (container: Document | HTMLElement): DataLayerEvent => ({
  event: DATA_LAYER_EVENTS.PRODUCT_LOAD,
  products: findProducts(container),
})

const screenEvent = ({ screen, user }: Smdl): DataLayerEvent => {
  const simType = getSimType()

  return {
    event: DATA_LAYER_EVENTS.SCREEN_LOAD,
    screen,
    user,
    groupInfo: buildGroupInfo(),
    ...(simType ? { sim_type: simType } : {}),
  }
}

const removeIfArrayEmpty = (
  event: DataLayerEvent,
  arrayName: keyof Pick<DataLayerEvent, 'modules' | 'products'>
) => {
  if (Array.isArray(event[arrayName]) && event[arrayName].length > 0) {
    return event
  }
  return undefined
}

function compact<T>(array: (T | undefined)[]): T[] {
  return array.filter((value): value is T => value !== undefined)
}
const buildEventsBasedOnScreenType = (
  screen: SmdlScreen | undefined,
  container: Document | HTMLElement,
  pageReady = true
): DataLayerEvent[] => {
  const { type = '', name = '' } = screen || {}
  let events: DataLayerEvent[] = []

  const contentDependentScreenTypes = [CHECKOUT, PRODUCT_DETAILS]
  if (type && !pageReady && contentDependentScreenTypes.includes(type)) {
    return []
  }

  switch (type) {
    case HOMEPAGE:
      return compact([removeIfArrayEmpty(moduleEvent(container), 'modules')])
    case PRODUCT_DETAILS:
      return compact([
        removeIfArrayEmpty(moduleEvent(container), 'modules'),
        removeIfArrayEmpty(productEvent(container), 'products'),
      ]).concat(
        findProducts(container).map((product) => ({
          product,
          event: DATA_LAYER_EVENTS.PRODUCT_VIEW,
        }))
      )
    case CHECKOUT:
      const cart = buildCart(container)
      const categoryType = cart?.lines?.[0]?.product?.category
      const payment = buildPaymentArray(container, categoryType)
      const { selectedPortInOption: selected_port_in_option } = cart || {}

      // Lets us our standard methods for getting these values rather than scraping the data attributes.
      const orderType = getOrderType()
      const leadGenerator = getLeadGenerator()

      if (orderType)
        if (name === 'checkout.plans-confirmation') {
          const transaction = buildTransaction(container)
          if (transaction && transaction.id) {
            if (
              cart?.value &&
              cart?.lines?.[0]?.product?.category ===
                PLAN_CATEGORIES.PRELOADED_SIM
            ) {
              cart.lines[0].product.value.displayGross = 0
              cart.lines[0].product.value.displayTax = 0
              cart.value.displayGross = 0
              cart.value.displayTax = 0
            }

            events = [
              {
                event: DATA_LAYER_EVENTS.TRANSACTION_SUCCESS,
                transaction: {
                  ...transaction,
                  cart: {
                    ...cart,
                    orderType,
                    leadGenerator,
                  },
                  payment,
                  lead_generator: leadGenerator,
                  order_type: orderType,
                  selected_port_in_option,
                },
              },
            ]
          } else {
            events = []
          }
        } else {
          events = []
          if (cart) {
            events.push({
              event: DATA_LAYER_EVENTS.CART_LOAD,
              order_type: SIM_JOURNEY_TYPES.ONLINE_SIM_ORDER_JOURNEY,
              cart,
            })
          }
        }
      return events
    default:
      return events
  }
}

const attachClickEvent = (
  _container: Document | HTMLElement,
  ele: HTMLElement,
  smdlObject: Smdl,
  clickEvent = 'data-dmpg-event-name'
) =>
  (ele.onclick = () =>
    smdlObject.events.push({
      event: ele.getAttribute(clickEvent) || '',
      ...convertDataAttributeToObjects(ele)?.event,
    }))

const attachAllClickEvents = (
  container: Document | HTMLElement,
  smdlObject: Smdl
) => {
  elements(container, '[data-dmpg-event-name]').forEach((el) =>
    attachClickEvent(container, el, smdlObject)
  )

  elements(container, '[data-item-id]').forEach((el) =>
    attachClickEvent(container, el, smdlObject, 'data-item-id')
  )
}

export const getScreenFromLocation = (): SmdlScreen | undefined => {
  const screen = findPageAttrs({
    path: removeTrailingSlash(window.location.pathname),
    pathMap,
    regexPathMap,
  })
  // Only include leadGenerator and orderType for certain screen types
  if (
    screen &&
    [CHECKOUT, PRODUCT_LIST, PRODUCT_DETAILS].includes(screen.type)
  ) {
    screen.lead_generator = getLeadGenerator()
    screen.order_type = getOrderType()
  }
  return screen
}

export const dmpgLayerNotAlreadyInitializedForThisScreen = (
  screen?: SmdlScreen,
  previouslyAttachedScreen?: string | null
) => screen && screen.name !== previouslyAttachedScreen

const completeInitializationForScreen = (
  completedEvents: DataLayerEvent[],
  smdlObject: Smdl,
  container: Document | HTMLElement
) => {
  completedEvents.forEach((completedEvent: DataLayerEvent) =>
    smdlObject.events.push(completedEvent)
  )
  attachAllClickEvents(container, smdlObject)
}

export const findLastScreenEvent = (
  smdlObject: Smdl
): DataLayerEvent | undefined =>
  (smdlObject.events || [])
    .filter((e) => e.event === DATA_LAYER_EVENTS.SCREEN_LOAD)
    .slice(-1)?.[0]

const matchScreen = (a?: SmdlScreen, b?: SmdlScreen) =>
  a?.name === b?.name && a?.type === b?.type

export const addScreenEventIfItChanged = (smdlObject: Smdl) => {
  const lastScreenEvent = findLastScreenEvent(smdlObject)
  if (
    !lastScreenEvent ||
    !matchScreen(smdlObject.screen, lastScreenEvent?.screen)
  ) {
    smdlObject.events.push(screenEvent(smdlObject))
  }
}

const buildDmpgDataAttributeListener = (
  container: Document | HTMLElement,
  smdlObject: Smdl,
  getScreen: () => SmdlScreen | undefined
) => {
  let previouslyAttachedScreen: string | null = null
  return () => {
    smdlObject.screen = getScreen() || smdlObject.screen
    smdlObject.user = buildUser()
    if (smdlObject.screen) {
      addScreenEventIfItChanged(smdlObject)
    }
    if (
      dmpgLayerNotAlreadyInitializedForThisScreen(
        smdlObject.screen,
        previouslyAttachedScreen
      )
    ) {
      const completedEvents = buildEventsBasedOnScreenType(
        smdlObject?.screen,
        container
      )

      if (completedEvents?.length > 0) {
        completeInitializationForScreen(completedEvents, smdlObject, container)
        previouslyAttachedScreen = smdlObject?.screen?.name || null
      }
    }
  }
}

export const attach = (
  container: Document | HTMLElement,
  smdlObject: Smdl,
  getScreen: () => SmdlScreen | undefined = getScreenFromLocation
) => {
  const dmpgListener = buildDmpgDataAttributeListener(
    container,
    smdlObject,
    getScreen
  )
  const observer = new MutationObserver(dmpgListener)
  observer.observe(container, { childList: true, subtree: true })
  return observer
}

const buildForm = (
  container: Document | HTMLElement = document,
  fields?: Record<string, string>[]
) => {
  const form = container.querySelector('form')
  if (form) {
    return {
      id: form.getAttribute('data-form-name'),
      fields,
    }
  }
  return undefined
}

const eventExtras = (
  eventName: string,
  container: Document | HTMLElement,
  fields?: Record<string, string>[]
) => {
  const form = buildForm(container, fields)

  if (form) return { form }

  if (eventName === DATA_LAYER_EVENTS.LOGIN_SUCCESS) {
    return { user: buildUser() }
  }

  return {}
}

export const addDmpgEvent = (
  eventObject: DataLayerEvent,
  container?: Document | HTMLElement,
  fields?: Record<string, string>[],
  groupInfo = {}
) => {
  window.smDl.events.push({
    ...eventObject,
    ...eventExtras(eventObject.event, container || document, fields),
    ...groupInfo,
    user: buildUser(),
  })
}

export const updateDmpgSmdl = (event: DataLayerEvent) => {
  event.user = buildUser()
  window.smDl?.events.push(event)
}

export const clearSmdlCart = () => {
  if (window.smDl) {
    delete window.smDl.cart
    delete window.smDl.product
  }
}

export const setSovendousData = (sessionID: string) => {
  if (window.smDl) {
    window.smDl.platform.sessionId = btoa(sessionID) // base64 encoding
  }
}

export const triggerPlansSectionEvent = (plans: Plan[], category: string) => {
  const assets: SmdlAssets = {
    type: category,
    products: [],
  }
  const items: ModuleItem[] = []

  plans.forEach((plan, index) => {
    assets.products?.push({
      id: plan.slug,
      name: plan.slug,
      category: category,
      value: {
        displayTax: returnV2CalculatedTax(
          plan.finalPriceWithoutCredit?.value
        ).toFixed(2),
        displayGross: plan.finalPrice.value,
        chargeType: 'monthly',
      },
      discount: {
        coupon: getOfferName(plan),
        value: {
          displayGross: getDiscountValue(plan),
        },
      },
    })
    items.push({
      id: plan.slug,
      type: 'products',
      position: (index + 1).toString(),
    })
  })

  triggerEvent({
    assets,
    event: DATA_LAYER_EVENTS.MODULE_LOAD,
    modules: [
      {
        id: 'select-' + category + '-list',
        items,
        name: 'select-' + category + '-list',
        placement: 'full-page',
      },
    ],
    user: buildUser(),
  })
}
